import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  ChatSession as ReduxChatSession,
  deleteSession,
  fetchSessions,
  setCurrentSession,
} from "../store/slices/chatSlice";
import { stripMarkdownForPreview } from "../utils/messageUtils";

interface ChatSidebarProps {
  open: boolean;
  onClose: () => void;
}

// Session list + new-chat + delete, ported from legacy Chatbot.tsx so the
// assistant-ui migration retains feature parity (see
// [[feedback-migration-feature-parity]]). All state lives in Redux —
// this is a presentational shell over the same actions.
export const ChatSidebar: React.FC<ChatSidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const { sessions, currentSession, loading } = useAppSelector((s) => s.chat);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchSessions());
  }, [dispatch]);

  const handleSelectSession = (sessionId: string) => {
    dispatch(setCurrentSession(sessionId));
    if (isMobile) onClose();
  };

  const handleCreateNewChat = () => {
    dispatch(setCurrentSession(null));
    if (isMobile) onClose();
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, chatId: string) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setSelectedChatId(chatId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedChatId(null);
  };

  const handleDeleteChat = () => {
    if (selectedChatId) {
      setSessionToDelete(selectedChatId);
      setDeleteConfirmOpen(true);
    }
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (sessionToDelete) {
      try {
        await dispatch(deleteSession(sessionToDelete)).unwrap();
        dispatch(fetchSessions());
      } catch (err) {
        console.error("Failed to delete session:", err);
      } finally {
        setDeleteConfirmOpen(false);
        setSessionToDelete(null);
      }
    }
  };

  const getLastMessagePreview = (session: ReduxChatSession): string => {
    if (
      session.last_message_preview &&
      typeof session.last_message_preview === "string"
    ) {
      const stripped = stripMarkdownForPreview(session.last_message_preview);
      const preview = stripped.substring(0, 50);
      return preview.length < stripped.length ? preview + "..." : preview;
    }
    if (session.messages && session.messages.length > 0) {
      const last = session.messages[session.messages.length - 1];
      if (last.content && typeof last.content === "string") {
        const stripped = stripMarkdownForPreview(last.content);
        const preview = stripped.substring(0, 50);
        return preview.length < stripped.length ? preview + "..." : preview;
      }
      return "Message loading...";
    }
    return "No messages yet";
  };

  const formatLastMessageDate = (session: ReduxChatSession): string => {
    let dateToUse = session.last_message_at || session.created_at;
    if (session.messages && session.messages.length > 0) {
      const last = session.messages[session.messages.length - 1];
      if (last.created_at) dateToUse = last.created_at;
    }
    try {
      return formatDistanceToNow(new Date(dateToUse), { addSuffix: true });
    } catch {
      return "Unknown";
    }
  };

  // `state.chat.loading` is overloaded — it flips true during both
  // `fetchSessions` (initial load) AND `sendStreamingMessage` (every
  // send). The latter would hide the entire sessions list with a
  // "Loading chats..." flash every time the user asks a question.
  // Gate the loader on having no sessions yet so it only shows
  // during the genuine initial fetch.
  const isLoadingSessions = loading && sessions.length === 0;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && open && (
        <Box
          onClick={onClose}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 999,
          }}
        />
      )}

      <Box
        sx={{
          width: isMobile ? (open ? "100%" : 0) : 280,
          // #fafafa (light) / #1a1a1a (dark) so the chat-history sidebar
          // reads as a secondary surface, not a peer to DashboardLayout's
          // app-nav rail (which uses background.paper). Per UX review.
          backgroundColor: (t) =>
            t.palette.mode === "dark" ? "#1a1a1a" : "#fafafa",
          borderRight: (t) => `1px solid ${t.palette.divider}`,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: isMobile ? "fixed" : "relative",
          zIndex: isMobile ? 1000 : "auto",
          transform: isMobile
            ? open
              ? "translateX(0)"
              : "translateX(-100%)"
            : "none",
          overflow: "hidden",
          transition: theme.transitions.create(["width", "transform"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              p: 2,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 500, color: "text.primary" }}
            >
              Chat History
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{ color: "text.primary", position: "absolute", right: 16 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        <Box
          sx={{ p: 2, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNewChat}
            disabled={isLoadingSessions}
            sx={{
              backgroundColor: "white",
              color: "#333",
              border: "1px solid #e0e0e0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              textTransform: "none",
              fontWeight: 500,
              py: 1.5,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              },
              "&:disabled": { backgroundColor: "#f5f5f5", color: "#999" },
            }}
          >
            New chat
          </Button>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 1,
            py: 1,
            "&::-webkit-scrollbar": { width: "12px" },
            "&::-webkit-scrollbar-track": {
              background: (t) =>
                t.palette.mode === "dark" ? "#2a2a2a" : "#f1f3f4",
              borderRadius: "6px",
              margin: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: (t) =>
                t.palette.mode === "dark" ? "#555" : "#c1c1c1",
              borderRadius: "6px",
              border: "2px solid transparent",
              backgroundClip: "content-box",
              "&:hover": {
                background: (t) =>
                  t.palette.mode === "dark" ? "#777" : "#a8a8a8",
                backgroundClip: "content-box",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: (t) =>
              t.palette.mode === "dark" ? "#555 #2a2a2a" : "#c1c1c1 #f1f3f4",
          }}
        >
          {isLoadingSessions ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Loading chats...
              </Typography>
            </Box>
          ) : sessions.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No chat sessions yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {sessions.map((session) => (
                <ListItem key={session.id} sx={{ p: 0, mb: 1 }}>
                  <ListItemButton
                    data-testid="chat-session-item"
                    onClick={() => handleSelectSession(session.id)}
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      flexDirection: "column",
                      alignItems: isMobile ? "center" : "flex-start",
                      textAlign: isMobile ? "center" : "left",
                      minHeight: "auto",
                      backgroundColor:
                        currentSession?.id === session.id
                          ? "rgba(163, 24, 48, 0.08)"
                          : "transparent",
                      border:
                        currentSession?.id === session.id
                          ? "1px solid rgba(163, 24, 48, 0.2)"
                          : "1px solid transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          currentSession?.id === session.id
                            ? "rgba(163, 24, 48, 0.12)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        mb: 0.5,
                      }}
                    >
                      <ChatIcon
                        sx={{
                          fontSize: 18,
                          color: "#666",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#333",
                          flexGrow: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.875rem",
                        }}
                      >
                        {session.title || `Chat ${session.id.substring(0, 8)}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, session.id)}
                        sx={{
                          ml: 1,
                          p: 0.5,
                          color: "#666",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#888",
                        fontSize: "0.75rem",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mb: 0.5,
                        pl: 3,
                      }}
                    >
                      {getLastMessagePreview(session)}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: "#aaa", fontSize: "0.7rem", pl: 3 }}
                    >
                      {formatLastMessageDate(session)}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: 2,
            minWidth: 120,
          },
        }}
      >
        <MenuItem
          onClick={handleDeleteChat}
          sx={{ fontSize: "0.875rem", color: "#d32f2f" }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
            <DeleteIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "0.875rem" }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSessionToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this chat? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setSessionToDelete(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{
              backgroundColor: "#d32f2f",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatSidebar;
